package org.mkui.canvas

import com.macrofocus.common.geom.Dimension
import com.macrofocus.common.geom.PreferredSize
import org.mkui.color.MkColor
import org.mkui.font.CPFontMetrics
import org.mkui.font.MkFont
import org.mkui.geom.PathIterator
import org.mkui.geom.Rectangle2D
import org.mkui.geom.Shape
import org.mkui.graphics.AbstractIGraphics
import org.mkui.graphics.IGraphics
import org.mkui.labeling.EnhancedLabel
import web.canvas.*
import kotlin.math.PI
import kotlin.math.ceil
import kotlin.math.max

class ElementalIGraphics(gc: CanvasRenderingContext2D) : AbstractIGraphics() {
    private val gc: CanvasRenderingContext2D
    val context2d: CanvasRenderingContext2D
        get() = gc

    override fun paintLabel(label: EnhancedLabel, x: Int, y: Int, w: Int, h: Int) {
        var y = y
        if (label.isOpaque) {
            gc.fillStyle = label.getBackground()!!
            gc.fillRect(x.toDouble(), y.toDouble(), w.toDouble(), h.toDouble())
        }
        val text: String? = label.getText()
        if (text != null) {
            //        gc.save();
            gc.textBaseline = CanvasTextBaseline.alphabetic
            //        gc.setTextBaseline(VPos.TOP);
            gc.font = label.getFont()!!.toString()
            // ToDo: setFillStyle and setStrokeStyle with color.getNativeColor() throw ClassCastException in SuperDevMode
            //        gc.setFillStyle(label.getForeground().getNativeColor());
            val javaScriptColor: MkColor? = label.getForeground()
            if(javaScriptColor != null) {
                gc.fillStyle = javaScriptColor
            }
            when (label.getRendering()) {
                EnhancedLabel.Rendering.WordWrap -> {
                    if (label.getVerticalAlignment() !== EnhancedLabel.TOP) {
                        val d = sizeLabel(label, w, h).preferredSize
                        when (label.getVerticalAlignment()) {
                            EnhancedLabel.CENTER -> y += ((h - d.height) / 2).toInt()
                            EnhancedLabel.BOTTOM -> y += (h - d.height).toInt()
                            else -> {
                            }
                        }
                    } else {
                    }
                    paintWordWrapText(
                        this,
                        label,
                        label.getText(),
                        w,
                        x,
                        y,
                        label.getForeground()!!,
                        label.getBackground()!!,
                        true
                    )
                }
                else -> {
                    if (label.getMinimumCharactersToDisplay() != null) {
                        val minimumCharactersToDisplay: Int = label.getMinimumCharactersToDisplay()!!.toInt()
                        if (text.length > minimumCharactersToDisplay) {
                            val s = text.substring(0, minimumCharactersToDisplay)
                            val textMetrics: TextMetrics = gc.measureText(s)
                            if (textMetrics != null) {
                                val width: Double = textMetrics.width
                                if (width > w - label.getInsetLeft() - label.getInsetRight()) {
                                    //                            gc.restore();
                                    return
                                }
                            }
                        }
                    }
                    fillText(label, x, y, w, text)
                }
            }
        }

//        gc.restore();
    }

    fun fillText(label: EnhancedLabel, x: Int, y: Int, w: Int, text: String) {
        when (label.getHorizontalAlignment()) {
            EnhancedLabel.LEADING, EnhancedLabel.LEFT -> {
                gc.textAlign = CanvasTextAlign.left
                gc.fillText(text, (x + label.getInsetLeft()).toDouble(), (y + label.getInsetTop() + getAscend(label)).toDouble())
            }
            EnhancedLabel.TRAILING, EnhancedLabel.RIGHT -> {
                gc.textAlign = CanvasTextAlign.right
                gc.fillText(text, (x + w - label.getInsetRight()).toDouble(), (y + label.getInsetTop() + getAscend(label)).toDouble())
            }
            EnhancedLabel.CENTER -> {
                gc.textAlign = CanvasTextAlign.left
                val width: Double = max(
                    0.0,
                    (w - label.getInsetLeft() - label.getInsetRight() - gc.measureText(text).width) / 2
                )
                gc.fillText(text, x + width + label.getInsetLeft(), (y + label.getInsetTop() + getAscend(label)).toDouble())
            }
        }
    }

    private fun paintWordWrapText(
        g: IGraphics?,
        label: EnhancedLabel,
        text: String?,
        maxWidth: Int,
        x: Int,
        yStart: Int,
        foreground: MkColor,
        background: MkColor,
        paint: Boolean
    ): Dimension {
        var maxWidth: Int = maxWidth
        var x: Int = x
        maxWidth -= label.getInsetLeft() + label.getInsetRight()
        var w: Float = label.getInsetLeft() .toFloat()+ label.getInsetRight().toFloat()
        x += label.getInsetLeft()
        var y: Int = yStart + label.getInsetTop()
        if ((maxWidth > 0) && (text != null) && (text.length > 0)) {
            if (g != null) {
//                    g.setFont(getFont());
//                    g.setRenderingHint(RenderingHints.KEY_TEXT_ANTIALIASING, RenderingHints.VALUE_TEXT_ANTIALIAS_ON);
//                    g.setRenderingHint(RenderingHints.KEY_FRACTIONALMETRICS, RenderingHints.VALUE_FRACTIONALMETRICS_OFF);
            }
            val words: MutableList<String> =
                ArrayList<String>(listOf<String>(*text.split(" ").toTypedArray()))
            var line: String = ""
            var lineCount: Int = 0
            var i: Int
            var test: String
            var testMetrics: TextMetrics? = null
            i = 0
            while (i < words.size) {
                test = words.get(i)
                testMetrics = gc.measureText(test)
                while (testMetrics!!.width > maxWidth) {
                    // Determine how much of the word will fit
                    test = test.substring(0, test.length - 1)
                    testMetrics = gc.measureText(test)
                }
                if (words.get(i) !== test) {
                    words.add(i + 1, words.get(i).substring(test.length))
                    words.set(i, test)
                }
                test = line + words.get(i) + ' '
                val previousMetrics: TextMetrics? = testMetrics
                testMetrics = gc.measureText(test)
                if (testMetrics.width > maxWidth && i > 0) {
                    if (paint) {
                        fillText(label, x, y, maxWidth, line)
                    }
                    line = words.get(i) + ' '
                    y += getLineHeight(label)
                    lineCount++
                    w = max(w, previousMetrics!!.width.toFloat())
                } else {
                    line = test
                }
                i++
            }
            if (paint) {
                fillText(label, x, y, maxWidth, line)
            }
            if (testMetrics != null) {
                w = max(w, testMetrics.width.toFloat())
            }
            y += getLineHeight(label)
        }
        return Dimension(
            ceil(w.toDouble()).toInt(),
            ceil(y.toDouble()).toInt() + label.getInsetBottom()
        )
    }

    override fun sizeLabel(label: EnhancedLabel, width: Int, height: Int): PreferredSize {
        return object : PreferredSize {
            override val minimumSize: Dimension
                get() = Dimension(0, getLineHeight(label))
            override val preferredHeight: Double
                get() = when (label.getRendering()) {
                    EnhancedLabel.Rendering.WordWrap -> {
                        gc.font = label.getFont()!!.toString()
                        paintWordWrapText(
                            this@ElementalIGraphics,
                            label,
                            label.getText(),
                            width,
                            0,
                            0,
                            label.getForeground()!!,
                            label.getBackground()!!,
                            false
                        ).height
                    }
                    else -> getLineHeight(label).toDouble()
                }
            override val preferredSize: Dimension
                get() {
                    gc.font = label.getFont()!!.toString()
                    return when (label.getRendering()) {
                        EnhancedLabel.Rendering.WordWrap -> paintWordWrapText(
                            this@ElementalIGraphics,
                            label,
                            label.getText(),
                            width,
                            0,
                            0,
                            label.getForeground()!!,
                            label.getBackground()!!,
                            false
                        )
                        else -> Dimension(gc.measureText(label.getText()!!).width.toInt(), getLineHeight(label))
                    }
                }
            override val maximumSize: Dimension
                get() = Dimension(Int.MAX_VALUE, Int.MAX_VALUE)
        }
    }

    fun getAscend(label: EnhancedLabel): Int {
        return 3 * getLineHeight(label) / 4
    }

    fun getLineHeight(label: EnhancedLabel): Int {
        return (label.getFont()!!.getSize2D() * 100 / 96 * 130 / 100).toInt()
    }

    override fun getLineWidth(): Double {
        return gc.lineWidth
    }

    override fun setLineWidth(lineWidth: Double) {
        gc.lineWidth = lineWidth
    }

    override fun setLineDash(dashPatern: FloatArray?) {
//        gc.setLineDash()
    }

    override fun setGlobalAlpha(alpha: Double) {
        gc.globalAlpha = alpha
    }

    override fun getColor(): MkColor {
        throw UnsupportedOperationException("getColor() not yet implemented")
    }

    override fun setColor(color: MkColor) {
        val javaScriptColor: String = color.toJavaScriptColor()
        gc.fillStyle = javaScriptColor
        gc.strokeStyle = javaScriptColor
    }

    override fun draw(geometry: Shape) {
        if (geometry is Rectangle2D) {
            drawRectangle(geometry)
        } else {
            drawPath(geometry)
            gc.stroke()
        }
    }

    override fun fill(geometry: Shape) {
        if (geometry is Rectangle2D) {
            fillRectangle2D(geometry)
        } else {
            drawPath(geometry)
            gc.fill()
        }
    }

    override fun drawLine(x1: Int, y1: Int, x2: Int, y2: Int) {
        gc.beginPath()
        gc.moveTo(x1.toDouble(), y1.toDouble())
        gc.lineTo(x2.toDouble(), y2.toDouble())
        gc.closePath()
        gc.stroke()
    }

    override fun drawLine(x1: Double, y1: Double, x2: Double, y2: Double) {
        gc.beginPath()
        gc.moveTo(x1, y1)
        gc.lineTo(x2, y2)
        gc.closePath()
        gc.stroke()
    }

    fun drawRectangle(rectangle: Rectangle2D) {
        gc.strokeRect(rectangle.x, rectangle.y, rectangle.width, rectangle.height)
    }

    override fun fillRectangle2D(rectangle: Rectangle2D) {
        gc.fillRect(rectangle.x, rectangle.y, rectangle.width, rectangle.height)
    }

    override fun fillCircle(rectangle: Rectangle2D) {
        gc.beginPath()
        gc.arc(rectangle.centerX, rectangle.centerY, rectangle.width / 2, 0.0, 2 * PI, false)
        gc.fill()
    }

    fun drawPath(path: Shape) {
        val iterator: PathIterator = path.getPathIterator()
        val coords = DoubleArray(4)
        gc.beginPath()
        while (!iterator.isDone) {
            when (val segment: Int = iterator.currentSegment(coords)) {
                PathIterator.SEG_MOVETO -> {
                    gc.moveTo(coords[0], coords[1])
                }
                PathIterator.SEG_LINETO -> {
                    gc.lineTo(coords[0], coords[1])
                }
                PathIterator.SEG_QUADTO -> {
                    gc.quadraticCurveTo(coords[0], coords[1], coords[2], coords[3])
                }
                PathIterator.SEG_CUBICTO -> {
                    gc.bezierCurveTo(coords[0], coords[1], coords[2], coords[3], coords[4], coords[5])
                }
                PathIterator.SEG_CLOSE -> {
                    gc.closePath()
                }
                else -> {
                    throw RuntimeException("Unknown Segment $segment")
                }
            }
            iterator.next()
        }
    }

    fun setFont(font: MkFont) {
        gc.font = font.toHtml()
    }

    override fun getFontMetrics(): CPFontMetrics? {
        return ElementalFontMetrics(gc)
    }

    override fun getStringWidth(text: String): Float {
        return gc.measureText(text).width.toFloat()
    }

    override fun getStringHeight(text: String): Float {
        return gc.measureText("M").width.toFloat()
    }

    val ascent: Double
        get() {
            val verticalTextMetrics: TextMetrics = gc.measureText("M")
            return verticalTextMetrics.width
        }
    val descent: Double
        get() = 0.0
    val fontMetrics: CPFontMetrics
        get() = ElementalFontMetrics(gc)

    override fun getStringBounds(text: String): Rectangle2D {
        val horizontalTextMetrics: TextMetrics = gc.measureText(text)
        val verticalTextMetrics: TextMetrics = gc.measureText("M")
        return Rectangle2D.Double(0.0, 0.0, horizontalTextMetrics.width, verticalTextMetrics.width)
    }

    override fun setTextBaseline(baseline: IGraphics.TextBaseline?) {
        when (baseline) {
            IGraphics.TextBaseline.Top -> gc.textBaseline = CanvasTextBaseline.Companion.top
            IGraphics.TextBaseline.Bottom -> gc.textBaseline = CanvasTextBaseline.Companion.bottom
            IGraphics.TextBaseline.Middle -> gc.textBaseline = CanvasTextBaseline.Companion.middle
            IGraphics.TextBaseline.Alphabetic -> gc.textBaseline = CanvasTextBaseline.Companion.alphabetic
            IGraphics.TextBaseline.Hanging -> gc.textBaseline = CanvasTextBaseline.Companion.hanging
            IGraphics.TextBaseline.Ideographic -> gc.textBaseline = CanvasTextBaseline.Companion.ideographic
            else -> throw IllegalArgumentException()
        }
    }

    override fun setFont(font: MkFont?) {
        gc.font = font!!.toHtml()
    }

    override fun drawString(text: String, x: Float, y: Float) {
        gc.fillText(text, x.toDouble(), y.toDouble())
    }

    override fun translate(x: Int, y: Int) {
        gc.translate(x.toDouble(), y.toDouble())
    }

    override fun rotate(theta: Double) {
        gc.rotate(theta)
    }

    override fun beginPath() {
        gc.beginPath()
    }

    override fun moveTo(x: Double, y: Double) {
        gc.moveTo(x, y)
    }

    override fun lineTo(x: Double, y: Double) {
        gc.lineTo(x, y)
    }

    override fun closePath() {
        gc.closePath()
    }

    override fun stroke() {
        gc.stroke()
    }

    override fun fill() {
        gc.fill()
    }

    init {
        this.gc = gc
    }
}
